import { BaseLayer, Build, BuildUpdateInfo, PaymentStatus } from "./types";
import { CurrencyCode } from "../utils/amountUtils";

import { CustomerPlatform } from "@final-commerce/common";

interface TaxesDetails {
    [id: string]: {
        amount: number;
        percentage: number;
        name: string;
        taxTableName: string;
        taxTableId: string;
        compounding?: boolean;
        priority?: number;
    };
}

export interface UpdateActiveEntityAction<E extends ActiveEntity> {
    entity: ActiveEntityType;
    data: E;
}

export interface ProductTaxDetails {
    tax: number;
    prodDiscount: number;
    cartDiscount: number;
    prodDiscountPercentage: number;
    cartDiscountPercentage: number;
    taxes: TaxesDetails;
    fee?: number;
    feeTax?: number;
    feePercentage?: number;
    feeLabel?: string;
    cartFee?: number;
    cartFeeTax?: number;
    cartFeePercentage?: number;
    cartFeeLabel?: string;
    feeTaxTableId?: string;
    cartFeeTaxTableId?: string;
    prodDiscountLabel?: string;
    /** Staging: line total including fee for discount allocation */
    lineDisplayWithFee?: number;
    cartDiscountAllocated?: number;
    lineAfterCartDiscount?: number;
    computedLineTotal?: number;
    computedLineTax?: number;
    feeDisplay?: number;
}

export interface CustomSaleDetails {
    tax: number;
    cartDiscount: number;
    cartDiscountPercentage: number;
    taxes: TaxesDetails;
    cartFee?: number;
    cartFeeTax?: number;
    cartFeePercentage?: number;
    cartFeeLabel?: string;
    cartFeeTaxTableId?: string;
    applyTaxes?: boolean;
    /** Staging: for discount allocation and order total */
    lineDisplayWithFee?: number;
    cartDiscountAllocated?: number;
    lineAfterCartDiscount?: number;
    computedLineTotal?: number;
    computedLineTax?: number;
}

/** Per-rate tax (same shape as line item taxes). Used for cart fee breakdown (gst, hst, etc.). */
export interface CartFeeTaxEntry {
    id: string;
    name: string;
    percentage: number;
    amount: number;
    taxTableId: string;
    taxTableName: string;
    compounding?: boolean;
    priority?: number;
}

export interface CartFeesDetails {
    name: string;
    amount: number;
    percentage: number;
    feeTax: number;
    taxTableId: string;
    taxName: string;
    /** Per-rate breakdown (gst, hst, etc.) so order/refund summary can show correct amounts per rate. */
    taxes?: CartFeeTaxEntry[];
}
export interface CalculatedTaxDetails {
    tax: number;
    total: number;
    subtotal: number;
    orderSubtotal: number;
    totalDiscount: number;
    /** Staging: cart discount amount and subtotal after discount */
    discountAmount?: number;
    subtotalAfterDiscount?: number;
    products: { [id: string]: ProductTaxDetails };
    customSales: { [id: string]: CustomSaleDetails };
    totalTaxes: TaxesDetails;
    cartFees: CartFeesDetails[];
}

export interface TipData {
    value: number;
    label?: string;
    isPercent: boolean;
}

export interface TipsDetails {
    total: number;
    tips: TipData[];
    currency?: string;
    noTipLabel?: string;
    customTipLabel?: string;
    selectedTip?: TipData;
    cashChange?: number;
    enabled?: boolean;
}

export interface GlobalBlocks {
    [gbId: string]: BaseLayer;
}

export interface ActiveEntitiesState {
    [ActiveEntityType.CART]: ActiveCart;
    [ActiveEntityType.USER]?: ActiveUser | null; //employee without email like cashier, with email will be in readlmdb
    [ActiveEntityType.ORDER]?: Partial<ActiveOrder> | null;
    [ActiveEntityType.TAX]: ActiveTaxDetails;
    [ActiveEntityType.OUTLET]?: ActiveOutlet;
    [ActiveEntityType.COMPANY]?: ActiveCompany;
    [ActiveEntityType.PRODUCT]?: ActiveProduct | null;
    [ActiveEntityType.CUSTOMER]?: Partial<ActiveCustomer> | null;
    [ActiveEntityType.CHANGES]: ActiveEntityChanges; //use to know what's been changed in the LSD for conditions
    [ActiveEntityType.SPLIT_PAYMENT]?: ActiveSplitPayment | null;
    [ActiveEntityType.CALCULATED_TAX]?: CalculatedTaxDetails | null;
    [ActiveEntityType.TIPS_DETAILS]?: TipsDetails | null;
    [ActiveEntityType.REFUND_DETAILS]?: ActiveRefundOrder | null;
    [ActiveEntityType.FULL_PRODUCT]?: Product | null;
    [ActiveEntityType.IS_AUTHENTICATED]?: boolean | null;
    [ActiveEntityType.TIMER]: ActiveTimer | null;
    [ActiveEntityType.UPDATED_ORDER_ACTIONS]: ActiveUpdatedOrder | null;
    [ActiveEntityType.BUILD]: Build | null;
    [ActiveEntityType.BUILD_UPDATE_INFO]: BuildUpdateInfo | null;
    [ActiveEntityType.GLOBAL_BLOCKS]: GlobalBlocks | null;
    [ActiveEntityType.ACTIVE_STATION]: ActiveStation | null;
    [ActiveEntityType.CUSTOM_SALE]?: ActiveCustomSales | null;
    [ActiveEntityType.ACTIVE_FLOW]?: Flow | null;
    [ActiveEntityType.ACTIVE_FLOW_VERSION_URL]: string | null;
}

export enum ActiveEntityType {
    TAX = "tax",
    CART = "cart",
    USER = "user",
    ORDER = "order",
    PRODUCT = "product",
    FULL_PRODUCT = "fullProduct",
    CUSTOMER = "customer",
    OUTLET = "outlet",
    CHANGES = "changes",
    SPLIT_PAYMENT = "splitPayment",
    CALCULATED_TAX = "calculatedTaxDetails",
    TIPS_DETAILS = "tipsDetails",
    REFUND_DETAILS = "refundDetails",
    COMPANY = "company",
    IS_AUTHENTICATED = "isAuthenticated",
    TIMER = "timer",
    UPDATED_ORDER_ACTIONS = "updatedOrderActions", //FOR ORDER UPDATE VIA ACTION
    BUILD = "activeBuild",
    BUILD_UPDATE_INFO = "buildUpdateInfo",
    GLOBAL_BLOCKS = "globalBlocks",
    ACTIVE_STATION = "activeStation",
    SMART_GRID = "smartGrid",
    CUSTOM_SALE = "customSale",
    ACTIVE_FLOW = "activeFlow",
    ACTIVE_FLOW_VERSION_URL = "activeFlowVersionUrl"
}
export enum EnvironmentDataKeys {
    NETWORK_INFO = "Network Info",
    DISPLAY = "Display",
    APP_INFO = "App Info",
    LOCATION = "Location",
    DEVICE_INFO = "Device Info",
    SYSTEM_HEALTH = "System Health",
    DEVELOPER = "Developer",
    PERIPHERAL_DEVICES = "Peripheral Devices"
}
export enum EnvironmentDataSubKeys {
    DEVICE_STATUS = "Device Status",
    LAST_ONLINE_TIME = "Last Online Time",
    NETWORK_TYPE = "Network Type",
    SIGNAL_STRENGTH = "Signal Strength",
    NETWORK_NAME = "Network name",
    IP_ADDRESS = "IP Address",

    SCREEN_RESOLUTION = "Screen Resolution",
    PIXEL_RATIO = "Pixel Ratio",
    ORIENTATION = "Orientation",

    APP_VERSION = "App Version",
    BUILD_NUMBER = "Build Number",
    BUNDLE_ID = "Bundle ID",
    INSTALLED = "Installed",
    LAST_UPDATED = "Last Updated",

    LOCATION_ACCESS = "Location Access",
    CURRENT_LOCATION = "Current Location",

    MODEL = "Model",
    SYSTEM_VERSION = "System Version",
    FIRMWARE_VERSION = "Firmware Version",
    SERIAL_NUMBER = "Serial Number",
    IMEI_NUMBER = "IMEI Number",
    MAC_ADDRESS = "MAC Address",
    ACTIVATION_TIME = "Activation Time",
    TIME_ZONE = "Time Zone",
    DEVICE_LANGUAGE = "Device Language",

    TOTAL_RAM = "Total RAM",
    BATTERY_LEVEL = "Battery Level",
    BATTERY_STATUS = "Battery Status",
    LOW_POWER_MODE = "Low Power Mode",
    AVERAGE_CPU_USAGE = "Average CPU Usage",
    CPU_SPEED = "CPU Speed",
    AVAILABLE_STORAGE = "Available Storage",
    TOTAL_STORAGE = "Total Storage",
    UPTIME = "Uptime",

    FLUTTER_VERSION = "Flutter Version",
    DART_VERSION = "Dart Version",
    ENVIRONMENT = "Environment",

    CONNECTED_PRINTER = "Connected Printer"
}

export type DynamicDataContext =
    | ActiveEntityType.PRODUCT
    | ActiveEntityType.CART
    | ActiveEntityType.ORDER
    | ActiveEntityType.CUSTOMER
    | "general"
    | "smartGrid";

export interface ActiveEntity {}

export interface ActiveTimer {
    initialTimer: string;
    currentTimer: string;
}

export interface ActiveUpdatedOrder {
    customerNote?: string;
    status?: string;
}

export interface Discount {
    value: number;
    label?: string;
    isPercent?: boolean;
}
export interface CustomFee {
    label: string;
    amount: number;
    isPercent: boolean;
    applyTaxes: boolean;
    taxTableId?: string;
}
export interface ActiveCartProduct {
    id: string;
    name: string;
    note?: string;
    price: number;
    quantity: number;
    discount?: Discount;
    taxTableId: string;
}

export interface ActiveCustomSales {
    id: string;
    name: string;
    applyTaxes: boolean;
    taxTableId?: string;
    quantity: number;
    price: number;
    discount?: any;
    fee?: any;
}
export interface ActiveCart extends ActiveEntity {
    tax?: number;
    total: number;
    subtotal: number;
    discount?: Discount;
    customFee?: CustomFee[];
    products: ActiveProduct[];
    customSales?: ActiveCustomSales[];
    remainingBalance?: number;
    amountToBeCharged: number;
    customer?: Partial<Customer | null> | null;
    orderNotes?: string;
    cartTotal?: number;
    orderTotal?: number;
    orderId?: string;
    currency?: CurrencyCode;
    minorUnits?: number;
}

export interface ActivePark extends ActiveCart {
    orderId: string;
    receiptId: string;
    servedBy: string | { _id?: string | undefined; firstName: string; lastName: string } | undefined;
    createdAt: string | number;
}

export interface ActiveOutlet extends ActiveEntity {
    address: string;
    address2: string;
    city: string;
    state: string;
    country?: string;
    taxId: string;
    postCode?: string;
    name?: string;
    stripe?: {
        locationId: string;
    };
    id: string;
    sequenceNumber: number;
    _id?: string;
    stripeAccountId: string;
}

export interface ActiveCompany extends ActiveEntity {
    id?: string;
    name?: string;
    logo?: string;
    settings?: any;
}

export interface Customer {
    _id: string;
    companyId: any;
    externalId?: string;
    email: string;
    firstName: string;
    lastName: string;
    // fromOliver?: boolean;
    source?: CustomerPlatform; // New Property added to Schema
    phone?: string;
    tags?: string[];
    metadata?: Record<string, string>[];
    notes?: CustomerNote[];
    billing: Address | null;
    shipping: Address | null;
}

export interface CustomerNote {
    createdAt: string;
    message: string;
}

export interface OrderNote {
    externalId?: string;
    author?: string;
    note?: string;
    customerNote?: boolean;
    addedByUser?: string;
    dateCreated?: string;
}

export interface CustomSale {
    customSaleId: string;
    name: string;
    price: number;
    quantity: number;
    applyTaxes: boolean;
    total: number;
    totalTax: number;
    taxes: {
        name: string;
        percentage: number;
        amount: number;
        taxTableName: string;
        taxTableId: string;
        id: string;
    }[];
    discount: {
        cartDiscount: {
            amount: number;
            percentage: number;
        };
    };
    fee: {
        cartFee: {
            amount: number;
            label: string;
            percentage: number;
            tax: number;
            taxTableId: string;
        };
    };
}

export interface RefundedCustomSale extends CustomSale {
    // action: string;
}

export interface Order {
    _id: string;
    receiptId?: string;
    companyId: string;
    externalId: string | null;
    status: string;
    customer: Partial<ActiveCustomer | null>;
    customerNote?: string;
    summary: Summary;
    cartDiscount: CartDiscountItem | null;
    cartFees: CartFeeItem[] | null;
    updatedAt?: string; //server side
    createdAt?: string;
    paymentMethods: PaymentMethod[];
    source: string;
    posData: PosDataItem | null;
    sessionId: string;
    metadata: MetadataItem[];
    notes?: OrderNote[] | null;
    billing: Address | null;
    shipping: Address | null;
    lineItems: LineItem[];
    customSales: CustomSale[];
    refund?: RefundItem[];
    balance: number;
    signature?: string | null;
    parkExpiryDate?: string;
    parkReduceStock?: string;
    currency?: CurrencyCode;
    minorUnits?: number;
}

export interface Summary {
    discountTotal: number;
    shippingTotal?: number | null;
    total: number;
    totalTaxes: number;
    subTotal: number;
    taxes: Tax[];
    tip?: Tip | null;
    isTaxInclusive: boolean;
}

interface Tip {
    amount: number;
    percentage: number;
}

interface CartDiscountItem {
    label: string;
    amount: number;
    percentage: number;
}

export interface CartFeeItem {
    id: string;
    label: string;
    amount: number;
    percentage: number;
    taxTableId?: string;
    tax?: number;
    taxName: string;
    /** Per-rate breakdown (gst, hst, etc.) so summary shows correct amounts per rate. */
    taxes?: CartFeeTaxEntry[];
}

export interface TipPayment {
    amount: number;
    tipTo: string;
    percentage: number;
}

export interface RefundedTipPayment {
    amount: number;
    percentage: number;
    transactionId: string;
    tipTo: string;
}

export interface PaymentMethod {
    transactionId: string;
    paymentType: string;
    amount: number;
    timestamp: string;
    processor: string;
    saleId?: string;
    change?: number | null;
    tip?: TipPayment | null;
    cashRounding?: number;
    emv?: string | null;
    processorFee?: number | null;
}

interface PosDataItem {
    outlet: string;
    station: string;
    employee: string | { _id?: string; firstName: string; lastName: string };
}

interface MetadataItem {
    key: string;
    value: string;
}

export interface Address {
    firstName: string;
    lastName: string;
    company?: string | null;
    city: string;
    postCode: string;
    province?: string;
    state?: string;
    country?: string;
    address1: string;
    address2: string;
}

export interface LineItem {
    productId: string;
    variantExternalId?: string;
    productExternalId?: string;
    internalId?: string;
    name: string;
    quantity: number;
    price: number;
    taxes: Tax[];
    discount: DiscountLineItem;
    fee: FeeLineItem;
    totalTax: number;
    total: number;
    metadata: MetadataItem[];
    image: string;
    sku: string;
    stock: number;
    note?: string;
    description?: string;
    variantId: string;
    images?: string[];
    attributes?: string;
}

export interface Tax {
    id: string;
    name: string;
    percentage: number;
    amount: number;
    taxTableName: string;
    taxTableId: string;
    compounding?: boolean;
    priority?: number;
}

interface DiscountLineItem {
    itemDiscount: DiscountDetail;
    cartDiscount: DiscountDetail;
}

interface FeeLineItem {
    itemFee: FeeDetail;
    cartFee?: FeeDetail;
}

interface DiscountDetail {
    percentage: number;
    amount: number;
    label?: string;
}

interface FeeDetail {
    percentage: number;
    amount: number;
    tax: number;
    label?: string;
    taxTableId: string;
}

export interface RefundItem {
    lineItems: RefundedLineItem[];
    customSales: RefundedCustomSale[];
    cartFees: CartFeeItem[];
    tips: RefundedTipPayment[];
    refundedBy: string;
    timestamp: string | undefined;
    summary?: Summary;
    refundPayment: PaymentMethod[];
    balance?: number;
    receiptId?: string;
    currency?: CurrencyCode;
    minorUnits?: number;
}

export interface RefundedLineItem {
    productId: string;
    variantId: string;
    variantExternalId?: string;
    productExternalId?: string;
    internalId?: string;
    name: string;
    quantity: number;
    price: number;
    taxes: Tax[];
    discount: DiscountLineItem;
    totalTax: number;
    total: number;
    image: string;
    sku: string;
    note?: string;
    description?: string;
    images?: string[];
    fee: FeeLineItem;
}

export interface ActiveOrder extends Order {
    id?: string;
    internalId?: string;
    user?: ActiveUser;
    outlet?: ActiveOutlet;
    isDeleted?: boolean;
    newOrder?: boolean;
    currency?: CurrencyCode;
    minorUnits?: number;
    station?: ActiveStation;
}

export interface ActiveStation {
    _id: string;
    sequenceNumber?: number;
    name: string;
    status: string;
    buildSrcId?: string;
    buildVersion?: string;
    publishBuildId?: string;
    createdAt?: string;
    updatedAt?: string;
    stripeTerminalId?: string;
    serialReaderId?: string;
    enrolledBy?: string;
    isVirtual?: boolean;
}

export interface Session {
    id: string;
    stationId: string;
    openingAmount?: number;
    closingAmount?: number;
    openedBy?: string;
    closedBy?: string;
    notes?: string[];
    currency?: CurrencyCode;
    minorUnits?: number;
}

export interface ActiveProduct extends ActiveEntity {
    id: string;
    internalId: string;
    externalId: string;
    productExternalId: string;
    variantId: string;
    name: string;
    sku: string;
    price: number;
    images: string[];
    taxTableId: string;
    quantity: number;
    note?: string;
    discount?: Discount;
    description?: string;
    longDescription?: string;
    shortDescription?: string;
    barcodeId?: string;
    stock: number;
    allowBackOrder?: boolean;
    fee?: CustomFee;
    currency?: CurrencyCode; // Added currency
    minorUnits?: number; // Added minorUnits
    isUnlimited?: boolean;
    attributes?: string;
    localQuantity?: number;
    _id?: string;
    productType?: ProductType;
}

export interface ActiveUserRole {
    id?: string; //Added this for condition i looked into code didn't see where this is saved TODO
    name: string;
    permissions: {
        category: string;
        label: string;
        name: string;
        value: boolean;
    }[];
}

export enum UserTypes {
    OWNER = "owner",
    ORG_USER = "organization_user",
    ORG_OWNER = "organization_owner",
    FINAL_USER = "final_user",
    FINAL_OWNER = "final_owner",
    EMPLOYEE = "employee",
    SUPER_ADMIN = "super_admin",
    MANAGER = "manager",
    ADMIN = "admin",
    ASSISTANT_MANAGER = "assistant",
    CASHIER = "cashier",
    RESELLER = "reseller"
}

export const FINAL_USERS = [UserTypes.FINAL_USER, UserTypes.FINAL_OWNER];

export const ORG_USERS = [UserTypes.ORG_USER, UserTypes.ORG_OWNER];
export const USERS_WHO_DONT_NEED_ROLE = [UserTypes.OWNER, ...FINAL_USERS, ...ORG_USERS];
export interface ActiveUser extends ActiveEntity {
    firstName?: string;
    lastName?: string;
    email?: string;
    role: ActiveUserRole;
    id: string;
    outlets?: string[];
    type?: UserTypes;
    companies?: any;
}
export interface ActiveCustomer extends Customer {
    id?: string;
}

export interface TaxTableActive {
    name: string;
    country: string;
    postcode: string;
    rate: number;
    priority: number;
    compounding: boolean;
    id: string;
    taxTableName: string;
    taxTableId: string;
}
export interface ActiveTaxDetails extends ActiveEntity {
    taxIncl?: boolean;
    tables: { [name: string]: TaxTableActive[] };
}

export interface ActiveEntityChanges {
    productInCart: string[] | null; //array of ids of product in cart
    numberOfProductsInCart: number;
    cartValue: number;
    user: string | null;
    customer: string | null;
    order: string | null;
    role: string | null;
    product: string | null;
}

export interface ActiveSplitPayment {
    amountRemaining?: number | null;
    amountToBeCharged?: number | null;
    splitNumber?: null | number;
    currentSplit?: number;
    status?: PaymentStatus;
    isFixed?: boolean;
    payments?: null | PaymentMethod[];
    paidAmount?: number;
    tip?: number | null;
}
export interface RefundProcessingStatus {
    status: PaymentStatus;
    isCardPresent?: boolean;
    message?: string;
}

export interface ActiveRefundOrder {
    quantities?: Record<string, number>;
    options?: Record<string, string>;
    refundAmounts?: Record<string, number>;
    refundPayments?: PaymentMethod[] | null;
    currentRefundTotal?: number;
    amountRemaining?: number | null;
    buttonsDisabled?: Record<string, boolean>;
    isRefund?: boolean;
    customSalesQuantities?: Record<string, number>;
    cartFeesRefunds?: Record<string, number>;
    tipsRefunds?: Record<string, number>;
    refundProcessingStatus?: RefundProcessingStatus | null;
    sessionRefundedTotal?: number;
}

export enum ProductType {
    SIMPLE = "simple",
    VARIABLE = "variable"
}

export interface Inventory {
    warehouse: string;
    outletId: string;
    stock?: number | null;
}

export interface ProductVariant {
    sku: string;
    price: number; // Changed to number (minor units)
    salePrice: number; // Changed to number (minor units)
    isOnSale: boolean;
    barcode?: string;
    costPrice?: number; // Changed to number (minor units)
    manageStock: boolean;
    externalId: string;
    inventory?: Inventory[];
    allowBackorder?: boolean;
    images?: string[];
    attributes: {
        name: string;
        value?: string;
    }[];
    metadata?: {
        key: string;
        value: string;
    }[];
    _id: string;
    isDeleted?: boolean;
    currency?: CurrencyCode; // Added currency
    minorUnits?: number; // Added minorUnits
}

export interface Product {
    _id: string;
    companyId?: string;
    externalId: string;
    taxTable: string;
    name: string;
    description?: string;
    shortDescription?: string;
    images?: string[];
    categories: {
        name: string;
        externalId: string;
    };
    attributes: {
        name: string;
        values: string[];
    }[];
    tags?: string[];
    supplier?: string;
    sku?: string;
    productType: ProductType;
    variants: ProductVariant[];

    minPrice?: number; // Changed to number (minor units)
    maxPrice?: number; // Changed to number (minor units)
    price?: number; // Changed to number (minor units)
    status?: string;
    isDeleted?: boolean;
    currency?: CurrencyCode; // Added currency
    minorUnits?: number; // Added minorUnits
    createdAt?: string;
    updatedAt?: string;
}

export interface ActiveFullProduct extends ActiveEntity, Product {}

export interface Flow {
    _id: string;
    companyId: string;
    title: string;
    description: string;
    template: string;
    createdBy: string;
    flowData: Record<string, any>;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    previewImage: string;
    currentVersion: string;
    webAppId: string;
}
